import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { GerinusService } from '../lib/gerinus.service';
import { RequestService } from './request.service';
import { OrganizationService } from './organization.service';
import { ProcessService } from './process.service';
import { UserService } from './user.service';
import { GlobalService } from '../lib/global.service';
import { Observable, of } from 'rxjs';
import { ApplicantRequestService } from './applicant-request.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DatePipe } from '@angular/common';
import { SecondsToDaysPipe } from 'src/app/date-to-days-pipe.pipe';

@Injectable()
export class DashboardService extends GerinusService {
	constructor(
		public http: HttpClient,
		public requestService: RequestService,
		public processService: ProcessService,
		public organizationService: OrganizationService,
		public userService: UserService,
		public global: GlobalService,
		public applicantRequestService: ApplicantRequestService,
		private datePipe: DatePipe
	) {
		super(http);
	}

	/**
	 * Todos os totais do painel inicial numa requisição só.
	 *
	 * Substitui dez chamadas independentes. Como cada requisição autenticada custa duas consultas
	 * de token antes de chegar ao trabalho útil, o ganho está em cortar requisições, não bytes.
	 *
	 * Os filtros não vão daqui: órgão, departamento e pessoa são lidos no servidor a partir do
	 * usuário autenticado.
	 */
	getTotaisPainel(): Observable<any> {
		return this.http.get(this.apiURL() + '/api/dashboard/totais', this.headers());
	}

	/**
	 * Total de solicitações de janeiro a dezembro do ano informado (ano corrente por padrão),
	 * opcionalmente filtrado por órgão e departamento. Responde `{ ano, totais: number[12] }`.
	 */
	getSolicitacoesPorMes(filtros: { ano?: number; orgao?: any; departamento?: any } = {}): Observable<any> {
		return this.http.get(this.apiURL() + '/api/dashboard/solicitacoes-por-mes' + this.toQueryString(filtros), this.headers());
	}

	/**
	 * Contadores do card "Alerta de Processos", opcionalmente filtrados por órgão e departamento.
	 * Responde `{ vencidos, proximosDoVencimento, semAtribuicao, emAndamento }`.
	 */
	getAlertasProcessos(filtros: { orgao?: any; departamento?: any } = {}): Observable<any> {
		return this.http.get(this.apiURL() + '/api/dashboard/alertas-processos' + this.toQueryString(filtros), this.headers());
	}

	/**
	 * Variação de cada card em relação ao mesmo trecho do mês anterior, opcionalmente filtrada
	 * por órgão e departamento. Cada chave responde `{ atual, anterior, percentual }`.
	 */
	getVariacaoMensal(filtros: { orgao?: any; departamento?: any } = {}): Observable<any> {
		return this.http.get(this.apiURL() + '/api/dashboard/variacao-mensal' + this.toQueryString(filtros), this.headers());
	}

	/**
	 * Os cinco órgãos com mais movimentações registradas no histórico nos últimos 30 dias.
	 * Responde `{ orgaos: [{ codigo, descricao, sigla, total, percentual }] }`.
	 */
	getOrgaosMaisAtivos(): Observable<any> {
		return this.http.get(this.apiURL() + '/api/dashboard/orgaos-mais-ativos', this.headers());
	}

	/**
	 * Os cinco registros mais recentes entre os históricos de solicitação, processo e processo interno.
	 * Responde `{ atividades: [{ tipo, numero, historico, data }] }`.
	 */
	getAtividadesRecentes(): Observable<any> {
		return this.http.get(this.apiURL() + '/api/dashboard/atividades-recentes', this.headers());
	}

	/** Monta `?chave=valor` ignorando filtros vazios, para não mandar `orgao=null` à API. */
	private toQueryString(filtros: Record<string, any>): string {
		const params = new URLSearchParams();
		for (const [chave, valor] of Object.entries(filtros)) {
			if (valor !== null && valor !== undefined && valor !== '') {
				params.set(chave, String(valor));
			}
		}

		return params.toString() ? `?${params}` : '';
	}

	/**
	 * Conta registros sem trazer os registros.
	 *
	 * `page = 0` desliga a paginação na API e devolve a tabela inteira só para se medir o
	 * `length` do array. Pedindo uma página normal, a contagem já vem pronta em `meta.total`
	 * e o corpo da resposta fica do tamanho de uma página.
	 */
	async getTotal(entity: string, where: string = ''): Promise<number> {
		return new Promise<number>((resolve) => {
			if (!this[entity]) {
				resolve(0);
				return;
			}

			this[entity].listAll(where, 1).subscribe({
				next: (response: any) => {
					resolve(response?.meta?.total ?? response?.data?.length ?? 0);
				},
				error: () => resolve(0),
			});
		});
	}

	/**
	 * Estes dois existem só para alimentar contadores: todos os consumidores leem apenas o
	 * tamanho da lista. Paginados, a contagem vem em `meta.total` sem trazer a tabela inteira.
	 */
	getTotalObservable(entity: string, where: string = ''): Observable<any> {
		return this[entity].listAll(where, 1)
	}

	getTotalObservableScreeningProcess(entity: string, where: string = ''): Observable<any> {
		return this[entity].listAll(where, 1)
	}

	
	generateListPDF(entitys: any): void {
		const listEntity: any = {
		  requests: {
			title: 'Total de Solicitações',
			titles: ['NÚMERO', 'CPF', 'SOLICITANTE', 'ASSUNTO', 'STATUS', 'DATA DA SOLICITAÇÃO'],
			columns: [
			  { key: 'SOL_NumeroSolicitacao' },
			  { key: 'PE_CPF', format: this.formatCpfCnpj },
			  { key: 'PE_Nome' },
			  { key: 'AS_Descricao' },
			  { key: 'SOL_Status', format: this.getStatusDescriptionRequest },
			  { key: 'SOL_DataSolicitacao', format: (date: any) => this.formatDateRequest(date) }
			]
		  },
		  process: {
			title: 'Total de Processos',
			titles: ['NUP', 'DATA DE RECEBIMENTO', 'ASSUNTO', 'DEPARTAMENTO', 'STATUS', 'RESPONSÁVEL', 'RECEBIDO'],
			columns: [
			  { key: 'PRC_NumeroProcesso' },
			  { key: 'PRC_DataRecebimento', format: (date: any) => this.formatDateProcess(date) },
			  { key: 'AS_Descricao' },
			  { key: 'nomeDepartamentoAtual' },
			  { key: 'Status', format: this.getStatusDescriptionProcess },
			  { key: 'responsavel' },
			  { key: 'time_in_current_status', format: (seconds: any) => this.formatDateDayHoursMin(seconds) }
			]
		  },
		  usuario: {
			title: 'Total de Usuários',
			titles: ['NOME', 'GRUPO DE USUÁRIO', 'ÓRGÃO', 'DEPARTAMENTO', 'VÍNCULO'],
			columns: [
			  { key: 'PE_Nome' },
			  { key: 'GRP_Descricao' },
			  { key: 'OR_Descricao' },
			  { key: 'DEP_Descricao' },
			  { key: 'USR_Vinculo', format: this.toUppercase }
			]
		  },
		  orgao: {
			title: 'Total de Órgãos',
			titles: ['ÓRGÃO', 'SIGLA'],
			columns: [
			  { key: 'OR_Descricao' },
			  { key: 'OR_Sigla' }
			]
		  },
		  unassignedProcess: {
			title: 'Processos sem atribuição',
			titles: ['NUP', 'DATA DE RECEBIMENTO', 'ASSUNTO', 'DEPARTAMENTO', 'STATUS', 'RECEBIDO'],
			columns: [
				{ key: 'PRC_NumeroProcesso' },
				{ key: 'PRC_DataRecebimento', format: (date: any) => this.formatDateProcess(date) },
				{ key: 'AS_Descricao' },
				{ key: 'nomeDepartamentoAtual' },
				{ key: 'Status', format: this.getStatusDescriptionProcess },
				{ key: 'time_in_current_status', format: (seconds: any) => this.formatDateDayHoursMin(seconds) }
			]
		  },
		  screeningProcess: {
			title: 'Processos abertos triagem',
			titles: ['NUP', 'DATA DE RECEBIMENTO', 'ASSUNTO', 'DEPARTAMENTO', 'STATUS', 'RESPONSÁVEL', 'RECEBIDO'],
			columns: [
				{ key: 'PRC_NumeroProcesso' },
				{ key: 'PRC_DataRecebimento', format: (date: any) => this.formatDateProcess(date) },
				{ key: 'AS_Descricao' },
				{ key: 'nomeDepartamentoAtual' },
				{ key: 'Status', format: this.getStatusDescriptionProcess },
				{ key: 'responsavel' },
				{ key: 'time_in_current_status', format: (seconds: any) => this.formatDateDayHoursMin(seconds) }
			]
		  }
		};
	
		const doc = new jsPDF('p', 'mm', 'a4');
		const pageWidth = doc.internal.pageSize.getWidth();
		const currentDate = this.datePipe.transform(new Date(), 'dd/MM/yyyy HH:mm:ss');
	
		let firstPage = true;
	
		doc.setFont('Inter-SemiBold');
		doc.setFontSize(14);
		doc.text('RELATÓRIO GERAL DO DASHBOARD', pageWidth / 2, 12, { align: 'center' });
	
		for (const key in entitys) {
		  const entityData = listEntity[key];
		  if (!entityData) continue;
	
		  if (!firstPage) doc.addPage();
		  firstPage = false;
	
		  doc.setFontSize(8);
		  doc.text(entityData.title, pageWidth / 2, 20, { align: 'center' });
	
		  const tableData = entitys[key].data.map((item: any) =>
			entityData.columns.map((col: any) =>
			  col.format ? col.format(item[col.key], item) : (item[col.key] ?? '')
			)
		  );
	
		  autoTable(doc, {
			head: [entityData.titles],
			body: tableData,
			startY: 25,
			margin: { top: 25 },
			styles: { valign: 'middle', fontSize: 8 },
			headStyles: { fontSize: 6 },
			bodyStyles: { fontSize: 6 }
		  });
	
		  const pageHeight = doc.internal.pageSize.height;
		  doc.setFontSize(8);
		  doc.text(`Gerado em: ${currentDate}`, 14, pageHeight - 10);
		}
	
		doc.save('Relatorio-dashboard.pdf');
	}

	  getStatusDescriptionRequest(currentRequestStatus: number) {
        const statusDescriptions = {
            0: 'Triagem',
            1: 'Aceita',
            2: 'Em correção',
            3: 'Anulada',
            4: 'Corrigida',
			5: 'Assinatura pendente'
        };

        return statusDescriptions[currentRequestStatus];
    }

	getStatusDescriptionProcess(processStatus: number): string {
        const statusDescriptions = {
            1: 'Novo',
            2: 'Em Andamento',
            3: 'Arquivado',
            4: 'Concluído',
            5: 'Em Correção',
            6: 'Vencido',
            7: 'Anulado',
            8: 'Respondido'
        };

        return statusDescriptions[processStatus];
    }

	private formatDateRequest(date: any): string {
		return this.datePipe.transform(date, 'dd/MM/yyyy HH:mm:ss') ?? '';
	}

	private formatDateDayHoursMin = (seconds: any) => {
		return new SecondsToDaysPipe().transform(seconds);
	}

	private formatDateProcess = (date:  any) => {
		return new DatePipe('pt-BR').transform(date, 'dd/MM/yyyy HH:mm:ss', 'UTC');
	}

	private toUppercase(value: string): string {
		return value ? value.toUpperCase() : '';
	  }
}
